import { type ReactElement, PropsWithChildren } from "react";
import ctl from "@netlify/classnames-template-literals";
import Text, { TextWeight } from "../Text";

export type TagTheme = "dark" | "light" | "medium" | "transparent";
export type TagVariant =
  | "exclusive"
  | "default"
  | "info"
  | "teal"
  | "negative"
  | "warning"
  | "success"
  | "popular"
  | "purple"
  | "therapy"
  | "professionalCoaching"
  | "dark";

type TextAlign = "left" | "center" | "right";
type Opacity = "opaque" | "semiOpaque" | "transparent";
export type TextSize = "5xl" | "2xl" | "xl" | "base" | "sm" | "xs";

interface Props extends PropsWithChildren {
  align?: TextAlign;
  backgroundOpacity?: Opacity;
  customBackGroundClass?: string;
  fillWidth?: boolean;
  size?: TextSize;
  theme?: TagTheme;
  variant?: TagVariant;
  weight?: TextWeight;
}

export default function Tag({
  align = "left",
  backgroundOpacity = "opaque",
  children,
  customBackGroundClass,
  fillWidth = false,
  size = "sm",
  theme = "light",
  variant = "default",
  weight = "regular",
}: Props): ReactElement {
  const className = getClasses(
    theme,
    variant,
    fillWidth,
    align,
    backgroundOpacity,
    customBackGroundClass,
  );

  return (
    <span className={className}>
      <Text variant="body" weight={weight} align={align} size={size}>
        {children}
      </Text>
    </span>
  );
}

export function getClasses(
  theme: TagTheme,
  variant: TagVariant,
  fillWidth: boolean,
  align: TextAlign,
  backgroundOpacity: Opacity,
  customBackGroundClass = "",
): string {
  const baseClasses = `inline-flex items-center px-1.5 py-1 rounded-md ${
    align === "center" ? "justify-center" : ""
  } ${fillWidth ? "w-full" : ""} ${customBackGroundClass}`;

  const backgroundOpacityClasses = {
    opaque: "bg-opacity-100",
    semiOpaque: "bg-opacity-70",
    transparent: "bg-opacity-0",
  };

  const variantClasses = {
    default: {
      dark: ctl(`bg-neutral-700 text-neutral-100`),
      medium: ctl(`bg-neutral-500 text-white`),
      light: ctl(`bg-neutral-100 text-neutral-700`),
      transparent: ctl(`bg-white/20 text-white`),
    },
    exclusive: {
      dark: ctl(`bg-neutral-850 text-white`),
      medium: ctl(`bg-neutral-500 text-white`),
      light: ctl(`bg-white text-neutral-900`),
      transparent: ctl(`bg-white/20 text-white`),
    },
    info: {
      dark: ctl(`bg-status-info-400 text-accent-blue-100 `),
      medium: ctl(`bg-status-info-200 text-black `),
      light: ctl(`bg-status-info-100 text-black `),
      transparent: ctl(`bg-white/20 text-white`),
    },
    teal: {
      dark: ctl(`bg-status-teal-700 text-status-teal-50`),
      medium: ctl(`bg-status-teal-500 text-white`),
      light: ctl(`bg-status-teal-50 text-status-teal-700`),
      transparent: ctl(`bg-white/20 text-white`),
    },
    negative: {
      dark: ctl(`bg-status-red-700 text-status-red-50`),
      medium: ctl(`bg-status-red-500 text-white`),
      light: ctl(`bg-status-error-50 text-status-error-400`),
      transparent: ctl(`bg-white/20 text-neutral-900`),
    },
    purple: {
      dark: ctl(`bg-purple-600 text-white`),
      medium: ctl(`bg-purple-500 text-white`),
      light: ctl(`bg-purple-50 text-neutral-700`),
      transparent: ctl(`bg-white/20 text-white`),
    },
    warning: {
      dark: ctl(`bg-status-yellow-700 text-status-yellow-50`),
      medium: ctl(`bg-status-yellow-500 text-white`),
      light: ctl(`bg-status-yellow-50 text-status-yellow-700`),
      transparent: ctl(`bg-white/20 text-white`),
    },
    success: {
      dark: ctl(`bg-status-green-700 text-status-green-50`),
      medium: ctl(`bg-status-success-300 text-neutral-900`),
      light: ctl(`bg-status-green-50 text-status-green-700`),
      transparent: ctl(`bg-white/20 text-white`),
    },
    // Only one theme for now
    popular: {
      dark: ctl(`bg-status-teal-500 text-white`),
      medium: ctl(`bg-status-teal-500 text-white`),
      light: ctl(`bg-status-teal-500 text-white`),
      transparent: ctl(`bg-white/20 text-white`),
    },
    therapy: {
      dark: ctl(`bg-accent-purple-400 text-purple-900`),
      medium: ctl(`bg-accent-purple-400 text-purple-900`),
      light: ctl(`bg-accent-purple-400 text-purple-900`),
      transparent: ctl(`bg-white/20 text-white`),
    },
    professionalCoaching: {
      dark: ctl(`bg-accent-blue-400 text-purple-900`),
      medium: ctl(`bg-accent-blue-400 text-purple-900`),
      light: ctl(`bg-accent-blue-400 text-purple-900`),
      transparent: ctl(`bg-white/20 text-white`),
    },
    dark: {
      dark: ctl(`bg-neutral-800 text-white`),
      medium: ctl(`bg-neutral-700 text-white`),
      light: ctl(`bg-neutral-600 text-white`),
      transparent: ctl(`bg-white/20 text-white`),
    },
  };

  return `${baseClasses} ${backgroundOpacityClasses[backgroundOpacity]} ${variantClasses[variant][theme]}`;
}
